import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/apiClient';
import { ChartBarIcon, BuildingIcon, UsersIcon, CurrencyDollarIcon, FlagIcon } from '../../components/Icons';
import EntitlementsManagement from './EntitlementsManagement';
import PricingManagement from './PricingManagement';
import TenantsManagement from './TenantsManagement';
import UserManagement from './UserManagement';

interface OverviewStats {
  total_users: number;
  active_tenants: number;
}

type AdminSection = 'pricing' | 'users' | 'tenants' | 'entitlements' | 'overview';

const AdminDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<AdminSection>('overview');
  const [stats, setStats] = useState<OverviewStats | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await apiClient.get('/admin/overview-stats/');
        setStats(data);
      } catch {
        setStats(null);
      }
    };
    fetchStats();
  }, []);

  const handleSignOut = () => {
    logout();
    navigate('/admin/login');
  };

  if (!user?.is_superuser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600">Only superusers can access the Admin Dashboard.</p>
        </div>
      </div>
    );
  }

  const navigationItems = [
    { id: 'overview' as AdminSection, name: 'Overview', icon: <ChartBarIcon size={5} /> },
    { id: 'tenants' as AdminSection, name: 'Tenants', icon: <BuildingIcon size={5} /> },
    { id: 'users' as AdminSection, name: 'Users', icon: <UsersIcon size={5} /> },
    { id: 'pricing' as AdminSection, name: 'Billing & Pricing', icon: <CurrencyDollarIcon size={5} /> },
    { id: 'entitlements' as AdminSection, name: 'Feature Flags & Entitlements', icon: <FlagIcon size={5} /> },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6 flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage tenants, billing, users, and feature flags
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">{user?.email}</span>
              <button
                onClick={handleSignOut}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-6">
          {/* Sidebar Navigation */}
          <div className="w-64 flex-shrink-0">
            <nav className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="space-y-1">
                {navigationItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveSection(item.id)}
                    className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                      activeSection === item.id
                        ? 'bg-indigo-50 text-indigo-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="mr-3 text-xl inline-flex items-center">{item.icon}</span>
                    {item.name}
                  </button>
                ))}
              </div>
            </nav>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            {activeSection === 'overview' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Platform Overview</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-green-600 mb-1">Total Users</p>
                        <p className="text-3xl font-bold text-green-900">{stats?.total_users ?? '-'}</p>
                      </div>
                      <div className="flex items-center justify-center"><UsersIcon size={6} className="text-green-600" /></div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-primary mb-1">Active Tenants</p>
                        <p className="text-3xl font-bold text-purple-900">{stats?.active_tenants ?? '-'}</p>
                      </div>
                      <div className="flex items-center justify-center"><BuildingIcon size={6} className="text-primary" /></div>
                    </div>
                  </div>
                </div>
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setActiveSection('pricing')}
                      className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors text-left"
                    >
                      <p className="font-medium text-gray-900">Billing & Pricing</p>
                      <p className="text-sm text-gray-500 mt-1">View tenants and subscription plans</p>
                    </button>
                    <button
                      onClick={() => setActiveSection('tenants')}
                      className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors text-left"
                    >
                      <p className="font-medium text-gray-900">View Tenants</p>
                      <p className="text-sm text-gray-500 mt-1">Manage all tenants</p>
                    </button>
                    <button
                      onClick={() => setActiveSection('entitlements')}
                      className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors text-left"
                    >
                      <p className="font-medium text-gray-900">Feature Flags</p>
                      <p className="text-sm text-gray-500 mt-1">View entitlements by tier</p>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'tenants' && <TenantsManagement />}
            {activeSection === 'users' && <UserManagement />}
            {activeSection === 'pricing' && <PricingManagement />}
            {activeSection === 'entitlements' && <EntitlementsManagement />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
